import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import {
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { formatDate } from 'date-fns'
import { formatCurrency, formatDateNoTz, formatNumber } from 'src/utils/formatter-utils'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import SerproScope from '../serpro.scopes'
import { Nfe } from '../types'

const LOG = Logger.get('SERPRO-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type SerproViewProps = {
    className?: string
    scope: SerproScope
}
class SerproViewClass implements FCClassContext<SerproViewProps> {
    // :: Fields
    scope!: SerproScope
    sytles!: ReturnType<typeof useStyles>
    chaveNfeInputRef!: React.MutableRefObject<HTMLInputElement | undefined>

    // :: emissors

    readonly emitOnFind = () => {
        this.scope.chaveNfeSelected = this.chaveNfeInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: SerproViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: SerproViewProps) {
        this.scope = bindUpdate(React, scope)
        this.chaveNfeInputRef = React.useRef<HTMLInputElement>()

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.chaveNfeInputRef}
                        label="Cole ou escreva a chave da NFe"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                </Box>
                {this.scope.nfe && <NFeViewer nfe={this.scope.nfe} />}
            </div>
        )
    }
}

export const PFView = classToFComponent(SerproViewClass, React)
export default PFView

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Card sx={{ mb: 2 }}>
        <CardContent>
            <Typography variant="h6" gutterBottom>
                {title}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {children}
        </CardContent>
    </Card>
)

function NFeViewer({ nfe }: { nfe: Nfe }) {
    const ide = nfe.nfeProc.NFe.infNFe.ide
    const emit = nfe.nfeProc.NFe.infNFe.emit
    const dest = nfe.nfeProc.NFe.infNFe.dest
    const produtos = nfe.nfeProc.NFe.infNFe.det
    const total = nfe.nfeProc.NFe.infNFe.total.ICMSTot
    const cobr = nfe.nfeProc.NFe.infNFe.cobr
    const eventos = nfe.procEventoNFe
    const prot = nfe.nfeProc.protNFe.infProt

    return (
        <Box p={2} key={prot.nProt}>
            {/* Cabeçalho e protocolo */}
            <Section title="Protocolo NF-e">
                <Typography>
                    <strong>Chave:</strong> {prot.chNFe}
                </Typography>
                <Typography>
                    <strong>Status:</strong> {prot.cStat} - {prot.xMotivo}
                </Typography>
                <Typography>
                    <strong>Protocolo:</strong> {prot.nProt}
                </Typography>
                <Typography>
                    <strong>Data Recebimento:</strong> {formatDate(prot.dhRecbto, 'dd/MM/yyyy HH:mm:ss')}
                </Typography>
            </Section>

            {/* Identificação da NF-e */}
            <Section title="Identificação da NF-e">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography>
                            <strong>Número:</strong> {ide.nNF}
                        </Typography>
                        <Typography>
                            <strong>Série:</strong> {ide.serie}
                        </Typography>
                        <Typography>
                            <strong>Modelo:</strong> {ide.mod}
                        </Typography>
                        <Typography>
                            <strong>Tipo:</strong> {ide.tpNF === 1 ? 'Saída' : 'Entrada'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography>
                            <strong>Natureza da Operação:</strong> {ide.natOp}
                        </Typography>
                        <Typography>
                            <strong>Data de Emissão:</strong> {formatDate(ide.dhEmi, 'dd/MM/yyyy HH:mm:ss')}
                        </Typography>
                        <Typography>
                            <strong>Data de Saída/Entrada:</strong> {formatDate(ide.dhSaiEnt, 'dd/MM/yyyy HH:mm:ss')}
                        </Typography>
                        <Typography>
                            <strong>Ambiente:</strong> {ide.tpAmb === 1 ? 'Produção' : 'Homologação'}
                        </Typography>
                    </Grid>
                </Grid>
            </Section>

            {/* Emitente */}
            <Section title="Emitente">
                <Typography>
                    <strong>{emit.xNome}</strong> ({emit.xFant})
                </Typography>
                <Typography>CNPJ: {emit.CNPJ}</Typography>
                <Typography>IE: {emit.IE}</Typography>
                <Typography>
                    {emit.enderEmit.xLgr}, {emit.enderEmit.nro} - {emit.enderEmit.xMun}/{emit.enderEmit.UF}
                </Typography>
            </Section>

            {/* Destinatário */}
            <Section title="Destinatário">
                <Typography>
                    <strong>{dest.xNome}</strong>
                </Typography>
                <Typography>CNPJ: {dest.CNPJ}</Typography>
                {dest.IE && <Typography>IE: {dest.IE}</Typography>}
                <Typography>
                    {dest.enderDest.xLgr}, {dest.enderDest.nro} - {dest.enderDest.xMun}/{dest.enderDest.UF}
                </Typography>
                <Typography>Email: {dest.email}</Typography>
            </Section>

            {/* Produtos */}
            <Section title="Produtos">
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Produto</TableCell>
                                <TableCell align="right">Qtd</TableCell>
                                <TableCell align="right">Unidade</TableCell>
                                <TableCell align="right">V. Unitário</TableCell>
                                <TableCell align="right">V. Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {produtos.map((item) => (
                                <TableRow key={item.nItem}>
                                    <TableCell>{item.nItem}</TableCell>
                                    <TableCell>{item.prod.xProd}</TableCell>
                                    <TableCell align="right">{formatNumber(item.prod.qCom)}</TableCell>
                                    <TableCell align="right">{item.prod.uCom}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.prod.vUnCom)}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.prod.vProd)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            {/* Totais */}
            <Section title="Totais">
                <Typography>
                    <strong>Produtos:</strong> {formatCurrency(total.vProd)}
                </Typography>
                <Typography>
                    <strong>Frete:</strong> {formatCurrency(total.vFrete)}
                </Typography>
                <Typography>
                    <strong>Descontos:</strong> {formatCurrency(total.vDesc)}
                </Typography>
                <Typography>
                    <strong>ICMS:</strong> {formatCurrency(total.vICMS)}
                </Typography>
                <Typography>
                    <strong>Valor NF:</strong> {formatCurrency(total.vNF)}
                </Typography>
            </Section>

            {/* Cobrança */}
            {cobr && (
                <Section title="Cobrança">
                    <Typography>
                        <strong>Fatura:</strong> {cobr.fat.nFat} - Valor: {formatCurrency(cobr.fat.vOrig)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1">Duplicatas:</Typography>
                    {cobr.dup.map((d) => (
                        <Typography key={d.nDup}>
                            {d.nDup} - Venc.: {formatDateNoTz(d.dVenc)} - Valor: {formatCurrency(d.vDup)}
                        </Typography>
                    ))}
                </Section>
            )}

            {/* Eventos */}
            {eventos.length > 0 && (
                <Section title="Eventos">
                    {eventos.map((ev, idx) => (
                        <Box key={idx} mb={1}>
                            <Chip label={ev.evento.infEvento.detEvento.descEvento} color="primary" />
                            <Typography>
                                Data: {formatDate(ev.evento.infEvento.dhEvento, 'dd/MM/yyyy HH:mm:ss')}
                            </Typography>
                            {ev.retEvento.infEvento.xMotivo && (
                                <Typography>Status: {ev.retEvento.infEvento.xMotivo}</Typography>
                            )}
                        </Box>
                    ))}
                </Section>
            )}
        </Box>
    )
}
